/**
 * I raggruppamenti dei pannelli dell'accettazione.
 *
 * Il server manda una riga per squadra; qui si ricavano le viste che i pannelli disegnano. Sono
 * poche decine di righe, quindi si raggruppa nel browser invece di chiedere al server tre tagli
 * diversi degli stessi dati.
 *
 * Tutto quello che sta qui e' puro: entra un array di righe, esce un array o un oggetto. E' la
 * parte che puo' sbagliare i conti, ed e' l'unica che ha senso provare da sola.
 */

/** Un numero che potrebbe non esserci: un backend piu' vecchio del frontend non manda il campo. */
const n = (valore) => valore ?? 0;

/**
 * Le squadre in ordine alfabetico, senza toccare l'array di partenza.
 *
 * Il server le manda per id, cioe' in ordine di iscrizione: va bene per la tabella grande, dove
 * l'ordine di arrivo dice qualcosa, ma nei pannelli si cerca una squadra per nome, e per cercarla
 * bisogna che stia dove uno se l'aspetta. localeCompare in italiano, cosi' gli accenti e le
 * maiuscole non buttano fuori posto le righe.
 */
export const perNome = (squadre) =>
  [...squadre].sort((a, b) => a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' }));

/** Le persone di una squadra: i giocatori in distinta piu' gli accompagnatori dichiarati. */
export const persone = (riga) => n(riga.giocatori) + n(riga.accompagnatori);

/**
 * I totali della riga di chiusura del pannello del cibo. <code>conIntolleranze</code> conta le
 * squadre che hanno scritto qualcosa, non le persone: il campo e' testo libero e non un numero.
 */
export const totaliCibo = (squadre) => squadre.reduce((acc, s) => ({
  squadre: acc.squadre + 1,
  persone: acc.persone + persone(s),
  giocatori: acc.giocatori + n(s.giocatori),
  accompagnatori: acc.accompagnatori + n(s.accompagnatori),
  celiaci: acc.celiaci + n(s.celiaci),
  vegetariani: acc.vegetariani + n(s.vegetariani),
  vegani: acc.vegani + n(s.vegani),
  conIntolleranze: acc.conIntolleranze + (s.intolleranze && s.intolleranze.trim() ? 1 : 0)
}), {
  squadre: 0, persone: 0, giocatori: 0, accompagnatori: 0,
  celiaci: 0, vegetariani: 0, vegani: 0, conIntolleranze: 0
});

/**
 * I totali del pannello delle persone nuove. Le due colonne restano separate perche' sono due
 * domande diverse: "e' la prima volta che gioca" e "e' la prima volta che gioca qui".
 */
export const totaliNuovi = (squadre) => squadre.reduce((acc, s) => ({
  giocatori: acc.giocatori + n(s.giocatori),
  nuoviAssoluti: acc.nuoviAssoluti + n(s.nuoviAssoluti),
  nuoviCircuito: acc.nuoviCircuito + n(s.nuoviCircuito)
}), { giocatori: 0, nuoviAssoluti: 0, nuoviCircuito: 0 });

/** Quante squadre sono nuove, quante tornano e quante non hanno un aggancio alla storica. */
export const contaStoriche = (squadre) => squadre.reduce((acc, s) => ({
  ...acc,
  [s.storica]: (acc[s.storica] ?? 0) + 1
}), { nuova: 0, ritorno: 0, nonAgganciata: 0 });

/**
 * Dei gruppi, quelli che hanno davvero un punto sulla Terra: gli altri la mappa non li sa mettere.
 *
 * L'elenco dei gruppi arriva gia' raggruppato dal server, che e' l'unico che puo' farlo: il
 * gruppo lo dichiara ogni persona, e le coordinate stanno nell'anagrafica dei gruppi, agganciata
 * per nome. Qui resta solo da separare chi si puo' disegnare da chi no.
 */
export const puntiMappa = (gruppi) =>
  gruppi.filter(g => typeof g.lat === 'number' && typeof g.lng === 'number');

/**
 * Le squadre raccolte per quota, in ordine di importo. L'incasso e' quota per giocatore, come la
 * colonna della tabella: gli accompagnatori non pagano la quota.
 */
export const perQuota = (squadre) => {
  const per = new Map();

  squadre.forEach(s => {
    const quota = n(s.quota);
    const esistente = per.get(quota) ?? { quota, squadre: 0, giocatori: 0, incasso: 0 };

    esistente.squadre += 1;
    esistente.giocatori += n(s.giocatori);
    esistente.incasso += quota * n(s.giocatori);
    per.set(quota, esistente);
  });

  return [...per.values()].sort((a, b) => a.quota - b.quota);
};
